import { Link, useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { Star, Heart } from "lucide-react";
import { useFav } from "@/context/FavContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

export default function GigCard({ gig, testId }) {
  const price = Math.min(...(gig.packages?.map((p) => p.price) || [0]));
  const { isFav, toggle } = useFav();
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const fav = isFav(gig.gig_id);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    const r = await toggle(gig.gig_id);
    if (r?.favorited) toast.success("Saved to favorites");
  };

  return (
    <Link
      to={`/gig/${gig.gig_id}`}
      data-testid={testId || `gig-card-${gig.gig_id}`}
      className="group block bg-card border border-black/10 hover:border-foreground transition-all duration-200 hover:-translate-y-0.5 hover:brutal-shadow-sm relative"
    >
      <button
        onClick={handleFav}
        data-testid={`fav-btn-${gig.gig_id}`}
        aria-label={fav ? "Unfavorite" : "Favorite"}
        className={`absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-background/95 border border-foreground/15 hover:border-foreground transition-all ${fav ? "text-primary" : "text-foreground"}`}
      >
        <Heart className={`w-4 h-4 ${fav ? "fill-primary" : ""}`} strokeWidth={1.75}/>
      </button>
      <div className="aspect-video overflow-hidden bg-muted">
        {gig.cover_image || gig.images?.[0] ? (
          <img
            src={fileUrl(gig.cover_image || gig.images?.[0])}
            alt={gig.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : null}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          {gig.seller_picture ? (
            <img src={gig.seller_picture} alt={gig.seller_name} className="w-6 h-6 rounded-full object-cover border border-foreground/20" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
              {gig.seller_name?.[0]}
            </div>
          )}
          <span className="text-xs text-muted-foreground">{gig.seller_name}</span>
        </div>
        <h3 className="text-base font-medium leading-snug mb-3 line-clamp-2 group-hover:text-primary transition-colors">
          {gig.title}
        </h3>
        <div className="flex items-center justify-between border-t border-black/10 pt-3">
          <div className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 fill-foreground text-foreground" />
            <span className="text-sm font-medium">{gig.rating_avg?.toFixed(1) || "New"}</span>
            <span className="text-xs text-muted-foreground">({gig.rating_count || 0})</span>
          </div>
          <div className="text-right">
            <div className="overline text-muted-foreground">FROM</div>
            <div className="mono font-semibold text-base">{format(price, { decimals: 0 })}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
